import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";

const LANGS = [
    { code: "al", label: "AL", cc: "al", name: "Shqip" },
    { code: "en", label: "EN", cc: "gb", name: "English" },
    { code: "it", label: "IT", cc: "it", name: "Italiano" },
];

const flagUrl = (cc, w = 40) => `https://flagcdn.com/w${w}/${cc}.png`;
const flagSrcSet = (cc, w) => `${flagUrl(cc, w)} 1x, ${flagUrl(cc, w * 2)} 2x`;

export default function LanguageSwitcher({ compact = false }) {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

    const change = (code) => {
        i18n.changeLanguage(code);
        localStorage.setItem("tc-lang", code);
        setOpen(false);
    };

    useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    if (compact) {
        return (
            <div className="flex items-center gap-1.5" data-testid="lang-switcher-compact">
                {LANGS.map((l) => {
                    const active = current.code === l.code;
                    return (
                        <button
                            key={l.code}
                            onClick={() => change(l.code)}
                            className={`flex items-center gap-1 px-1.5 py-0.5 transition-all ${
                                active
                                    ? "ring-1 ring-white/90 bg-white/10"
                                    : "opacity-70 hover:opacity-100"
                            }`}
                            title={l.name}
                            data-testid={`lang-${l.code}`}
                        >
                            <img
                                src={flagUrl(l.cc, 20)}
                                srcSet={flagSrcSet(l.cc, 20)}
                                alt={l.name}
                                width={18}
                                height={13}
                                className="block"
                                style={{ imageRendering: "auto" }}
                            />
                            <span className="text-[10px] font-mono font-bold text-white">
                                {l.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="relative" ref={ref} data-testid="lang-switcher">
            <button
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-1.5 px-2 py-1 border border-slate-200 hover:border-slate-400 text-xs font-mono bg-white"
                data-testid="lang-switcher-button"
            >
                <img src={flagUrl(current.cc, 20)} srcSet={flagSrcSet(current.cc, 20)} alt={current.name} width={18} height={13} />
                <span>{current.label}</span>
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-slate-300 shadow-lg min-w-[160px] z-50" data-testid="lang-dropdown">
                    {LANGS.map((l) => (
                        <button
                            key={l.code}
                            onClick={() => change(l.code)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 ${
                                current.code === l.code ? "bg-slate-100 font-bold" : ""
                            }`}
                            data-testid={`lang-option-${l.code}`}
                        >
                            <img src={flagUrl(l.cc, 40)} srcSet={flagSrcSet(l.cc, 40)} alt={l.name} width={22} height={16} />
                            <span>{l.name}</span>
                            <span className="ml-auto text-[10px] text-slate-500">{l.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
